/**
 * SlaAssignmentsTab Component
 *
 * Feature: 126-client-assignment-views (T012)
 * Description: Tab component showing SLA tier assignment for a client.
 *   Displays current SLA card with tier name and effective metrics,
 *   collapsible history section, and empty state.
 *   Data source: Existing slaService methods (no new API needed).
 * Constitution: v1.12.0 — Principle XXXVII (Code Header Standards), IV (SLA Tracking)
 */

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, ChevronDown, ChevronRight, AlertCircle, Clock, Pencil } from 'lucide-react';
import * as slaService from '../../services/slaService';
import type { ClientSLAAssignmentWithTier } from '@shared/types/sla';
import { DualScrollTable, usePagination, PaginationControls } from '../common/TableControls';
import { useAuth } from '../../modules/backup/hooks/useAuth';
import { formatEasternDate } from '@/utils/dateFormatter';
import { Button, Select } from '../ui';
import { roleSatisfies } from '../../../../shared/access/roles';

interface SlaAssignmentsTabProps {
  clientId: string;
}

export const SlaAssignmentsTab: React.FC<SlaAssignmentsTabProps> = ({ clientId }) => {
  // State
  const [showHistory, setShowHistory] = useState(false);

  // Fetch current assignment
  const {
    data: currentAssignment,
    isLoading: isCurrentLoading,
    error: currentError,
  } = useQuery({
    queryKey: ['client', clientId, 'sla', 'current'],
    queryFn: () => slaService.getCurrentAssignment(clientId),
    staleTime: 30_000,
    enabled: !!clientId,
  });

  // Fetch assignment history (only when expanded)
  const {
    data: history = [],
    isLoading: isHistoryLoading,
    error: historyError,
  } = useQuery({
    queryKey: ['client', clientId, 'sla', 'history'],
    queryFn: () => slaService.getAssignmentHistory(clientId),
    staleTime: 30_000,
    enabled: !!clientId && showHistory,
  });

  // Spec 178 US2 — set/override SLA (Admin-only; /api/sla assignTierToClient supersedes the prior)
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = roleSatisfies(user?.role, ['Admin']);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState('');
  const { data: tiers = [] } = useQuery({
    queryKey: ['sla', 'tiers'],
    queryFn: () => slaService.getTiers(),
    enabled: isEditing,
    staleTime: 60_000,
  });
  const assignMutation = useMutation({
    mutationFn: (slaTierId: string) =>
      slaService.createAssignment({ client_id: clientId, sla_tier_id: slaTierId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', clientId, 'sla'] });
      setIsEditing(false);
      setSelectedTierId('');
    },
  });

  const renderEditor = () =>
    isEditing ? (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-4">
        <p className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">Set SLA tier</p>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedTierId}
            onChange={(e) => setSelectedTierId(e.target.value)}
            options={[
              { value: '', label: 'Select a tier…' },
              ...tiers.map((t) => ({ value: t.id, label: t.tier_name })),
            ]}
            className="w-48"
          />
          <Button
            onClick={() => selectedTierId && assignMutation.mutate(selectedTierId)}
            disabled={!selectedTierId || assignMutation.isPending}
          >
            {assignMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" onClick={() => { setIsEditing(false); setSelectedTierId(''); }}>
            Cancel
          </Button>
        </div>
        {assignMutation.isError && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            {(assignMutation.error as Error).message}
          </p>
        )}
      </div>
    ) : null;

  // Handlers
  const handleToggleHistory = useCallback(() => {
    setShowHistory((prev) => !prev);
  }, []);

  // Format date for display
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return formatEasternDate(dateString);
  };

  // Format metric value with appropriate precision
  const formatMetric = (value: number | null | undefined, suffix: string = ''): string => {
    if (value === null || value === undefined) return '—';
    return `${value}${suffix}`;
  };

  // Filter history to exclude current assignment
  // When currentAssignment is null, show all history items
  const historicalAssignments = history.filter(
    (h) => !currentAssignment || h.id !== currentAssignment.id
  );

  // Loading state
  if (isCurrentLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent)]" />
      </div>
    );
  }

  // Error state
  if (currentError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-600 dark:text-red-400">
        <AlertCircle className="w-8 h-8 mb-2" />
        <p>Failed to load SLA assignment</p>
        <p className="text-sm text-[var(--color-text-secondary)]">{(currentError as Error).message}</p>
      </div>
    );
  }

  // Empty state
  if (!currentAssignment) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-[var(--color-text-secondary)]">
        <Shield className="w-12 h-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">No SLA tier assigned to this client.</p>
        <p className="text-sm mt-1">
          <a
            href="/settings/sla"
            className="text-[var(--color-accent)] hover:opacity-90"
          >
            Manage SLA assignments
          </a>
        </p>
        {isAdmin && !isEditing && (
          <Button
            variant="outline"
            leftIcon={<Pencil className="h-4 w-4" />}
            onClick={() => setIsEditing(true)}
            className="mt-4"
          >
            Set SLA
          </Button>
        )}
        {isEditing && <div className="mt-4 w-full max-w-md">{renderEditor()}</div>}
        {/* History toggle for empty current (may have historical assignments) */}
        <button
          onClick={handleToggleHistory}
          className="mt-4 text-sm text-[var(--color-accent)] hover:opacity-90"
        >
          {showHistory ? 'Hide history' : 'Show history'}
        </button>
        {/* Show historical assignments if toggled */}
        {showHistory && historicalAssignments.length > 0 && (
          <div className="mt-6 w-full max-w-2xl">
            <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">
              Previous Assignments
            </h3>
            <HistoryTable assignments={historicalAssignments} formatDate={formatDate} formatMetric={formatMetric} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current SLA Card */}
      <div className="bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
                {currentAssignment.tier_name}
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)]">
                Current SLA Tier
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-sm text-[var(--color-text-secondary)]">
              <Clock className="w-4 h-4" />
              <span>Effective since {formatDate(currentAssignment.effective_date)}</span>
            </div>
            {currentAssignment.expiration_date && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                Expires {formatDate(currentAssignment.expiration_date)}
              </p>
            )}
            {isAdmin && !isEditing && (
              <Button
                variant="outline"
                leftIcon={<Pencil className="h-4 w-4" />}
                onClick={() => setIsEditing(true)}
                className="mt-2"
              >
                Change SLA
              </Button>
            )}
          </div>
        </div>

        {/* SLA Metrics Grid */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="Uptime Target"
            value={formatMetric(currentAssignment.effective_uptime_percent, '%')}
            description="Minimum availability"
          />
          <MetricCard
            label="RTO"
            value={formatMetric(currentAssignment.effective_rto_hours, ' hrs')}
            description="Recovery Time Objective"
          />
          <MetricCard
            label="RPO"
            value={formatMetric(currentAssignment.effective_rpo_hours, ' hrs')}
            description="Recovery Point Objective"
          />
          <MetricCard
            label="Penalty Rate"
            value={currentAssignment.effective_penalty_per_hour_usd !== null
              ? `$${currentAssignment.effective_penalty_per_hour_usd}/hr`
              : '—'
            }
            description="SLA breach penalty"
          />
        </div>
      </div>

      {renderEditor()}

      {/* History Section (Collapsible) */}
      <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
        <button
          onClick={handleToggleHistory}
          className="w-full px-4 py-3 flex items-center justify-between bg-[var(--color-bg-subtle)] hover:opacity-80 transition-colors"
        >
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            Assignment History
            {historicalAssignments.length > 0 && (
              <span className="ml-2 text-[var(--color-text-secondary)]">({historicalAssignments.length})</span>
            )}
          </span>
          {showHistory ? (
            <ChevronDown className="w-4 h-4 text-[var(--color-text-secondary)]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[var(--color-text-secondary)]" />
          )}
        </button>

        {showHistory && (
          <div className="p-4 border-t border-[var(--color-border)]">
            {isHistoryLoading ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent)]" />
              </div>
            ) : historyError ? (
              <div className="text-center py-6 text-red-600 dark:text-red-400">
                <p>Failed to load history</p>
              </div>
            ) : historicalAssignments.length === 0 ? (
              <p className="text-center py-6 text-[var(--color-text-secondary)]">
                No previous assignments
              </p>
            ) : (
              <HistoryTable assignments={historicalAssignments} formatDate={formatDate} formatMetric={formatMetric} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/** Metric display card component */
interface MetricCardProps {
  label: string;
  value: string;
  description: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, description }) => (
  <div className="bg-[var(--color-bg-subtle)] rounded-lg p-4">
    <p className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
      {label}
    </p>
    <p className="mt-1 text-xl font-semibold text-[var(--color-text-primary)]">
      {value}
    </p>
    <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
      {description}
    </p>
  </div>
);

/** History table component */
interface HistoryTableProps {
  assignments: ClientSLAAssignmentWithTier[];
  formatDate: (date: string | null | undefined) => string;
  formatMetric: (value: number | null | undefined, suffix?: string) => string;
}

const HistoryTable: React.FC<HistoryTableProps> = ({ assignments, formatDate, formatMetric }) => {
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } = usePagination(assignments);
  return (
  <div>
    <DualScrollTable>
    <table className="w-full text-sm">
      <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
        <tr>
          <th className="px-4 py-2 text-left font-medium text-[var(--color-text-secondary)]">
            Tier Name
          </th>
          <th className="px-4 py-2 text-left font-medium text-[var(--color-text-secondary)]">
            Effective Date
          </th>
          <th className="px-4 py-2 text-left font-medium text-[var(--color-text-secondary)]">
            End Date
          </th>
          <th className="px-4 py-2 text-right font-medium text-[var(--color-text-secondary)]">
            Uptime
          </th>
          <th className="px-4 py-2 text-right font-medium text-[var(--color-text-secondary)]">
            RTO
          </th>
          <th className="px-4 py-2 text-right font-medium text-[var(--color-text-secondary)]">
            RPO
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--color-border)]">
        {pageItems.map((assignment) => (
          <tr key={assignment.id} className="opacity-70">
            <td className="px-4 py-2 text-[var(--color-text-primary)]">
              {assignment.tier_name}
            </td>
            <td className="px-4 py-2 text-[var(--color-text-secondary)]">
              {formatDate(assignment.effective_date)}
            </td>
            <td className="px-4 py-2 text-[var(--color-text-secondary)]">
              {formatDate(assignment.expiration_date)}
            </td>
            <td className="px-4 py-2 text-right text-[var(--color-text-secondary)]">
              {formatMetric(assignment.effective_uptime_percent, '%')}
            </td>
            <td className="px-4 py-2 text-right text-[var(--color-text-secondary)]">
              {formatMetric(assignment.effective_rto_hours, 'h')}
            </td>
            <td className="px-4 py-2 text-right text-[var(--color-text-secondary)]">
              {formatMetric(assignment.effective_rpo_hours, 'h')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </DualScrollTable>
    <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
  </div>
  );
};

export default SlaAssignmentsTab;
